// Change #100664 - "need a button to view the eSIM QR code ... and also email
// it to someone". The LPA activation code comes from emnify on demand (it is
// sensitive and never stored); the QR is drawn here with `qrcode`, and the same
// PNG is what gets emailed.
import React, { useEffect, useState } from "react";
import { Button, FormGroup, Input, Label, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import QRCode from "qrcode";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import AppModal from "../../../../Components/Common/AppModal";
import {
  getMasterSimEsimCode,
  postMasterSimEsimEmail,
} from "../../../../helpers/fakebackend_helper";

const EsimQrModal = ({ sim, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [code, setCode] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!sim?.Id) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setCode(null);
    setQrDataUrl("");
    setEmailTo("");
    setMessage("");
    (async () => {
      const res = await getMasterSimEsimCode(sim.Id);
      if (cancelled) return;
      if (res?.status === 200 && res?.activation_code) {
        setCode(res);
        try {
          const url = await QRCode.toDataURL(res.activation_code, {
            width: 320,
            margin: 2,
            errorCorrectionLevel: "M",
          });
          if (!cancelled) setQrDataUrl(url);
        } catch (e) {
          if (!cancelled) setError("Could not draw the QR code");
        }
      } else {
        setError(res?.message || "Could not get the eSIM code from emnify");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [sim?.Id]);

  const simName = sim?.Label || (sim?.Iccid ? `SIM ${sim.Iccid}` : "SIM");

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code?.activation_code || "");
      toast.success("Activation code copied");
    } catch (e) {
      toast.error("Could not copy - select the code and copy it by hand");
    }
  };

  const sendEmail = async () => {
    if (!emailTo.trim() || !qrDataUrl) return;
    setSending(true);
    const res = await postMasterSimEsimEmail({
      MasterSim_Id: sim.Id,
      EmailTo: emailTo.trim(),
      Message: message,
      QrPngBase64: qrDataUrl,
    });
    setSending(false);
    if (res?.status === 200) {
      toast.success(res?.message || "eSIM emailed");
      setEmailTo("");
      setMessage("");
    } else {
      toast.error(res?.message || "The email could not be sent");
    }
  };

  return (
    <AppModal
      open={!!sim}
      onClose={onClose}
      title="eSIM QR code"
      subtitle={simName}
      icon={<QrCode2Icon />}
      headerAccent="violet"
      size="sm"
      hideFooter
    >
      {loading ? (
        <div className="text-center text-muted py-4">
          <Spinner size="sm" className="me-2" />
          Getting the eSIM code from emnify...
        </div>
      ) : error ? (
        <div className="alert alert-warning mb-0">{error}</div>
      ) : (
        code && (
          <>
            <div className="text-center">
              {qrDataUrl && (
                <img
                  src={qrDataUrl}
                  alt={`eSIM QR code for ${simName}`}
                  style={{ width: 240, height: 240 }}
                />
              )}
              <div className="small text-muted mt-1">
                Scan from the phone: Settings &gt; Mobile/Cellular &gt; Add eSIM
                &gt; Use QR code.
              </div>
            </div>

            <Label className="mt-3 mb-1">Activation code</Label>
            <div className="d-flex gap-2 align-items-start">
              <code
                className="flex-grow-1 small text-break p-2 border rounded"
                style={{ userSelect: "all" }}
              >
                {code.activation_code}
              </code>
              <Button
                color="light"
                size="sm"
                className="border"
                onClick={copyCode}
                title="Copy the activation code"
              >
                <i className="ri-file-copy-line"></i>
              </Button>
            </div>
            {code.apple_universal_link && (
              <div className="small mt-2">
                <i className="ri-apple-fill me-1"></i>
                iPhone install link:{" "}
                <a
                  href={code.apple_universal_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  open on the iPhone
                </a>
              </div>
            )}

            <hr />
            <FormGroup className="mb-2">
              <Label for="esimEmailTo" className="mb-1">
                Email it to
              </Label>
              <Input
                id="esimEmailTo"
                type="email"
                bsSize="sm"
                placeholder="name@example.com"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
              />
            </FormGroup>
            <FormGroup className="mb-2">
              <Label for="esimEmailMessage" className="mb-1">
                Message (optional)
              </Label>
              <Input
                id="esimEmailMessage"
                type="textarea"
                rows={2}
                bsSize="sm"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </FormGroup>
            <div className="d-flex justify-content-between align-items-center">
              <span className="small text-muted">
                Sends the QR as a PNG, plus the code and iPhone link.
              </span>
              <Button
                color="primary"
                size="sm"
                onClick={sendEmail}
                disabled={sending || !emailTo.trim() || !qrDataUrl}
              >
                {sending ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="ri-mail-send-line align-middle me-1"></i>
                )}
                Send
              </Button>
            </div>
          </>
        )
      )}
    </AppModal>
  );
};

export default EsimQrModal;
